import { Link } from "react-router-dom";
import { useSEO } from "../lib/useSEO";
import { Mountain, ShieldCheck, MapPin } from "lucide-react";

export default function About() {
  useSEO({
    title: "About WaterfallFinder — Honest waterfall guides for travellers",
    description:
      "WaterfallFinder is a curated, honest guide to the world's most stunning waterfalls — with trail info, maps and safety advice for every visit.",
  });

  return (
    <main className="pt-16">
      {/* Hero */}
      <section className="relative h-[420px] md:h-[500px]" data-testid="about-hero">
        <img
          src="https://images.unsplash.com/photo-1469125155630-7ed37e065743?w=2000&q=70"
          alt="Forest waterfall"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <p className="text-white/85 text-sm font-semibold uppercase tracking-wider mb-3">About us</p>
          <h1 className="text-white text-5xl md:text-7xl font-extrabold tracking-tight max-w-4xl leading-[0.95]">
            Find waterfalls, safely.
          </h1>
        </div>
      </section>

      {/* Mission */}
      <section className="max-w-[1000px] mx-auto px-4 sm:px-6 lg:px-10 pt-16">
        <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-stone-900 max-w-3xl">
          The honest waterfall guide we always wanted.
        </h2>
        <div className="mt-8 max-w-3xl space-y-5 text-lg text-stone-700 leading-relaxed">
          <p>
            WaterfallFinder was born from a simple frustration: most travel sites lump waterfalls into glossy
            top-10 lists with no map, no real directions and no warning about why people actually get hurt at the
            sites they recommend.
          </p>
          <p>
            Our guides cover the waterfalls people actually search for — from Niagara and Multnomah to Wallaman, Aira
            Force and Skogafoss — and we&apos;re continually adding more. If you can&apos;t find a waterfall on the
            site, we probably haven&apos;t got to it yet. Let us know.
          </p>
        </div>
      </section>

      {/* What we do */}
      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 pt-20">
        <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-stone-900 mb-10">What we cover</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          <Feature
            icon={Mountain}
            title="Real waterfalls"
            body="Hand-curated guides for falls you'll actually want to visit — not auto-scraped clones of Wikipedia."
          />
          <Feature
            icon={MapPin}
            title="Maps & directions"
            body="Every waterfall has a working map and a one-tap directions link. No more guessing where to park."
          />
          <Feature
            icon={ShieldCheck}
            title="Safety first"
            body="Site-specific risk notes plus a general safety guide informed by national park guidance."
          />
        </div>
      </section>

      {/* Contact */}
      <section className="max-w-[1000px] mx-auto px-4 sm:px-6 lg:px-10 pt-20" data-testid="about-contact">
        <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-stone-900">Get in touch</h2>
        <p className="mt-6 text-lg text-stone-700 leading-relaxed max-w-2xl">
          Spotted an inaccuracy, want to suggest a waterfall, or just want to say hi? Email us at{" "}
          <a
            href="mailto:startupinvestmentgroup@gmail.com"
            className="text-stone-900 font-semibold underline underline-offset-4 hover:no-underline"
            data-testid="about-contact-email"
          >
            startupinvestmentgroup@gmail.com
          </a>
          {" "}— we read everything.
        </p>
      </section>

      {/* CTA */}
      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-20 mb-16">
        <div className="rounded-3xl bg-stone-900 text-white p-10 md:p-14 text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight text-white">Ready to find your next waterfall?</h2>
          <p className="mt-4 text-stone-300 max-w-xl mx-auto">
            Browse 90+ curated waterfall guides with photos, maps and safety info.
          </p>
          <div className="mt-7 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/waterfalls" className="btn-at btn-at-green" data-testid="about-cta-explore">
              Explore waterfalls
            </Link>
            <Link to="/safety" className="btn-at border-2 border-white text-white hover:bg-white hover:text-stone-900" data-testid="about-cta-safety">
              Read safety guide
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

function Feature({ icon: Icon, title, body }) {
  return (
    <div>
      <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center mb-4">
        <Icon className="w-5 h-5 text-stone-900" />
      </div>
      <h3 className="font-extrabold text-lg text-stone-900 mb-2">{title}</h3>
      <p className="text-stone-600 text-sm leading-relaxed">{body}</p>
    </div>
  );
}
